import React, { useState } from "react";
import { IoChevronDownOutline } from "react-icons/io5";
import { readToggles, writeToggles } from "../../utils/sessionToggles";
import { UNASSIGNED } from "../../config/countries";
import { geoTypeLabel } from "../../config/products";
import { groupAreasByCountry, groupByType } from "../../utils/areaCountryGroups";

const ChevronIcon = IoChevronDownOutline as any;

export interface BaseArea {
  id: string;
  type: string;
  country_code?: string | null;
}

interface AreaGroupsProps<T extends BaseArea> {
  areas: T[];
  /** Render a single area card (AreaCard). */
  renderCard: (area: T) => React.ReactNode;
  /** Start the type groups closed. They start open by default. */
  defaultCollapsed?: boolean;
  /** Given, a group the user opens or closes is remembered for the session under this key. */
  persistKey?: string;
  /** Level of the country headings; the type headings are one level lower. Default 2. */
  headingLevel?: 2 | 3;
}

const COUNT_PILL = "inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700";

/**
 * The areas of the Library and of every picker, grouped by country and then by area type. Each type is a
 * collapsible section holding a grid of cards. A closed group draws no cards, so a long closed list
 * costs nothing.
 */
function AreaGroups<T extends BaseArea>({
  areas,
  renderCard,
  defaultCollapsed = false,
  persistKey,
  headingLevel = 2,
}: AreaGroupsProps<T>) {
  const [toggled, setToggled] = useState<Record<string, boolean>>(() => readToggles(persistKey));
  const CountryHeading = `h${headingLevel}` as "h2";
  const TypeHeading = `h${headingLevel + 1}` as "h3";

  const isOpen = (key: string) => toggled[key] ?? !defaultCollapsed;
  const toggle = (key: string) => {
    const next = { ...toggled, [key]: !isOpen(key) };
    setToggled(next);
    writeToggles(persistKey, next);
  };

  const typeSection = (groupKey: string, country: Parameters<typeof geoTypeLabel>[1], type: string, list: T[]) => {
    const key = `${groupKey}-${type}`;
    const open = isOpen(key);
    return (
      <div key={key} className="overflow-hidden rounded-2xl border border-paper-300/70 bg-surface shadow-soft">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => toggle(key)}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-paper-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <TypeHeading className="font-display text-lg font-semibold text-ink">{geoTypeLabel(type, country)}</TypeHeading>
            <span className={COUNT_PILL}>{list.length}</span>
          </div>
          <ChevronIcon
            className={`h-5 w-5 text-ink-faint transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && (
          <div className="border-t border-paper-200 p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((area) => (
                <React.Fragment key={area.id}>{renderCard(area)}</React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {groupAreasByCountry(areas).map((group) => {
        const byType = groupByType(group.areas);

        return (
          <section key={group.key}>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xl">{group.flag}</span>
              <CountryHeading data-testid="area-country-heading" className="font-display text-2xl font-bold text-ink">
                {group.label}
              </CountryHeading>
              <span className="inline-flex items-center rounded-full bg-paper-200 px-2.5 py-1 text-xs font-mono text-ink-subtle">
                {group.areas.length}
              </span>
            </div>
            {group.key === UNASSIGNED && (
              <p className="text-sm text-ink-muted -mt-2 mb-4">
                These areas were saved without a country. Edit an area to choose one.
              </p>
            )}

            <div className="space-y-3">
              {byType.map(([type, list]) => typeSection(group.key, group.country, type, list))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default AreaGroups;
